import type { Recording } from './types'

/**
 * 从别的站点（华仁谋士聊天里的"去听录音"）跳进来的深链：
 *   https://learn.logging.top/#/?date=2026-10-20&sso=<token>
 * date 指定要听哪一天的录音；sso 是对方站点当前的登录 token（两个站点是不同域名，localStorage 不共享，
 * 但 token 是同一套会话，后端读同一个 SESSIONS KV，所以带过来就能直接用）。
 * 都放在 hash 里：hash 不会发给服务器、不进访问日志。
 */
const hashParams = (hash: string): URLSearchParams => {
  const queryStart = hash.indexOf('?')
  return new URLSearchParams(queryStart === -1 ? '' : hash.slice(queryStart + 1))
}

/** 读出 sso token 并立刻从地址栏抹掉（避免留在历史记录/被截图分享），其它参数保留。没有时返回 null */
export const consumeSsoToken = (): string | null => {
  const params = hashParams(window.location.hash)
  const token = params.get('sso')
  if (!token) return null
  params.delete('sso')
  const rest = params.toString()
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/${rest ? `?${rest}` : ''}`)
  return token
}

export interface DeepLink {
  /** 要听哪一天（YYYY-MM-DD） */
  date: string
  /** 具体哪个 mp3（同一天分段时有 "xxx[2].mp3"）；没有时听当天第一段 */
  file: string | null
  /** 从文件内第几秒开始播；没有时按默认（续播或从头） */
  startSec: number | null
}

/** 深链里要听的录音；没有日期或日期格式不对时返回 null */
export const readDeepLink = (): DeepLink | null => {
  const params = hashParams(window.location.hash)
  const date = params.get('date')
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const t = params.get('t')
  const startSec = t !== null && /^\d+$/.test(t) ? Number(t) : null
  return { date, file: params.get('f') || null, startSec }
}

/** 把深链参数从地址栏去掉（已经处理过了，刷新页面不再自动打开），其它参数保留 */
export const clearDeepLink = (): void => {
  const params = hashParams(window.location.hash)
  if (!params.has('date') && !params.has('f') && !params.has('t')) return
  params.delete('date')
  params.delete('f')
  params.delete('t')
  const rest = params.toString()
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/${rest ? `?${rest}` : ''}`)
}

/** 某一天的录音（同一天可能有多段，按文件名排序，第一段排前面） */
export const recordingsForDate = (recordings: Recording[], date: string): Recording[] =>
  recordings.filter(recording => recording.recordedAt === date).sort((a, b) => a.name.localeCompare(b.name))

/** 深链指向的那一段：优先按文件名匹配，匹配不上（或没给文件名）就取当天第一段；当天没有录音返回 undefined */
export const pickRecording = (recordings: Recording[], link: DeepLink): Recording | undefined => {
  const sameDay = recordingsForDate(recordings, link.date)
  return (link.file ? sameDay.find(recording => recording.name === link.file) : undefined) ?? sameDay[0]
}
